// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import type { BankUploadForm } from '@client/banks/types/create-bank-form';
import type { BankUploadStep } from '@client/banks/types/bank-upload';
import type { SvgIconName } from '@shared/ui-kit/components/app-icon/app-icon';
import type { TFunction } from 'i18next';

export type ReviewChecklistItemId =
  | 'accounts'
  | 'positions'
  | 'debts'
  | 'transactions';

export type ReviewChecklistStatus = 'complete' | 'pending';

export type ReviewChecklistItem = {
  id: ReviewChecklistItemId;
  icon: SvgIconName;
  listStep: BankUploadStep;
  sectionLabel: string;
  countLabel: string;
  status: ReviewChecklistStatus;
};

const CHECKLIST_SECTIONS = [
  {
    id: 'accounts',
    icon: 'Coins',
    listStep: 'accounts-list',
    labelKey: 'aggregator.create-bank.review.section-label.accounts',
    countKey: 'aggregator.create-bank.review.count.accounts',
  },
  {
    id: 'positions',
    icon: 'ChartLine',
    listStep: 'positions-list',
    labelKey: 'aggregator.create-bank.review.section-label.positions',
    countKey: 'aggregator.create-bank.review.count.positions',
  },
  {
    id: 'debts',
    icon: 'Banknote',
    listStep: 'debts-list',
    labelKey: 'aggregator.create-bank.review.section-label.debts',
    countKey: 'aggregator.create-bank.review.count.debts',
  },
  {
    id: 'transactions',
    icon: 'ArrowLeftRight',
    listStep: 'transactions-list',
    labelKey: 'aggregator.create-bank.review.section-label.transactions',
    countKey: 'aggregator.create-bank.review.count.transactions',
  },
] as const satisfies ReadonlyArray<{
  id: ReviewChecklistItemId;
  icon: SvgIconName;
  listStep: BankUploadStep;
  labelKey: string;
  countKey: string;
}>;

const getSectionCount = (
  form: BankUploadForm,
  id: ReviewChecklistItemId,
): number => {
  switch (id) {
    case 'accounts':
      return form.accounts.length;
    case 'positions':
      return form.positions?.length ?? 0;
    case 'debts':
      return form.debts?.length ?? 0;
    case 'transactions':
      return form.transactions?.length ?? 0;
  }
};

/**
 * Review-step checklist rows derived from wizard form counts.
 *
 * @param form - Wizard aggregate passed to submit.
 * @param t - I18next `TFunction` for section labels and pluralized counts.
 */
export const buildReviewChecklist = (
  form: BankUploadForm,
  t: TFunction,
): ReviewChecklistItem[] =>
  CHECKLIST_SECTIONS.map(section => {
    const count = getSectionCount(form, section.id);

    return {
      id: section.id,
      icon: section.icon,
      listStep: section.listStep,
      sectionLabel: t(section.labelKey, { ns: 'aggregator' }),
      countLabel: t(section.countKey, { ns: 'aggregator', count }),
      status: count > 0 ? 'complete' : 'pending',
    };
  });

/** True when at least one checklist section has no rows yet. */
export const hasPendingSections = (items: ReviewChecklistItem[]): boolean =>
  items.some(item => item.status === 'pending');
